// Import package components
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import UIkit from "uikit";
import {
  Accordion,
  AccordionItem,
  Flex,
  Image,
  InputContainer,
  Link,
} from "uikit-react";
// import { useForm } from "react-hook-form";
// import * as yup from "yup";
// import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import classnames from "classnames";

// Import base components
import { AppContexts } from "../../../providers";

// Import common components
import SMSEmailInputbox from "../../common/sms-email-inputbox";
import Tags from "../../common/Tags";
import SelectTemplate from "../../common/Template";
import ConvertUTCDateToLocalDate from "../../common/ConvertUTCDateToLocalDate";
import { analyzeSmsMessage } from "../../../utils/smsMessageLength";
import { getAdvancedConditions } from "../conditions";
import {
  MessageLengthEditorStatus,
  MessageLengthInlineStatus,
} from "../../common/SmsMessageLengthStatus";

const SchedulerAdd = () => {
  const {
    loading,
    message,
    setMessage,
    initMessage,
    isNew,
    setIsEditing,
    insertScheduledMessage,
    updateScheduledMessage,
    sources,
  } = useContext(AppContexts.SchedulerContext);

  const { settings } = useContext(AppContexts.SettingsContext);
  const { rentals } = useContext(AppContexts.RentalsContext);
  const { user } = useContext(AppContexts.AuthContext);
  const showPmsAdvancedOptions = [
    "Streamline",
    "LiveRez",
    "Hostaway",
    "OwnerRez",
  ].includes(user?.details?.pms);

  const [template, setTemplate] = useState("");
  const [msg, setMsg] = useState({});
  const [errors, setErrors] = useState([]);
  const offcanvasRef = useRef(null);
  const requiredFields = ["name", "body"];
  const [scheduleType, setScheduleType] = useState("unscheduled");
  // Store msg state for each scheduleType
  const [msgByType, setMsgByType] = useState({});

  useEffect(() => {
    const element = offcanvasRef.current;
    // A modal controller can move this form outside React's event root.
    // Own a single offcanvas controller for the lifetime of this page.
    const offcanvas = UIkit.offcanvas(element, { container: false });
    const handleBeforeHide = (event) => {
      if (event.target === element) setIsEditing(false);
    };
    element.addEventListener("beforehide", handleBeforeHide);
    return () => {
      element.removeEventListener("beforehide", handleBeforeHide);
      offcanvas.$destroy();
    };
  }, [setIsEditing]);

  const doValidate = useCallback(() => {
    let errs = [];
    requiredFields.map((field) => {
      if (!msg[field].trim()) {
        errs.push(field);
      }
    });

    setErrors(errs);
    return errs.length === 0;
  }, [msg]);

  useEffect(() => {
    const defaultSendDay = isNew ? 99999 : message?.send_day;
    const initialMsg = {
      ...JSON.parse(JSON.stringify(message)),
      send_day: defaultSendDay ?? 99999,
    };
    setMsg(initialMsg);
    setMsgByType({}); // Reset on new message
    setErrors([]);
  }, [message, isNew]);

  useEffect(() => {
    const sd = msg?.send_day;

    if (sd === undefined || sd === null) {
      return;
    }

    if (sd === 99998) {
      setScheduleType("email-upon-booking");
    } else if (sd === 99999) {
      setScheduleType("unscheduled");
    } else if (sd >= -29 && sd <= 29) {
      if (msg?.reference_point !== "in" && msg?.reference_point !== "out") {
        setScheduleType('day-of-week');
      } else {
        setScheduleType("check-in-out");
      }
    } else if (sd >= 99990 && sd <= 99997) {
      setScheduleType("day-of-week");
    } else if (sd >= 99900 && sd <= 99929) {
      setScheduleType("extend");
    } else {
      setScheduleType("unknown");
    }
  }, [msg?.send_day, msg?.reference_point]);

  const filteredRentals = useMemo(() => {
    if (!rentals) {
      return [];
    } else {
      if (Array.isArray(msg.tags) && msg.tags.length > 0) {
        return rentals.filter((r) => {
          return (
            !!r &&
            !!msg.tags.filter(
              (t1) => !!(r.tags || []).find((t2) => t1.id === t2.id),
            )[0]
          );
        });
      } else {
        return rentals.filter((r) => !!r);
      }
    }
  }, [msg.tags, rentals]);

  const options = useMemo(() => {
    return msg.options;
  }, [msg]);

  const messageLength = useMemo(
    () =>
      analyzeSmsMessage(msg.body || "", {
        CompanyName: settings.branding?.company_name,
      }),
    [msg.body, settings.branding?.company_name],
  );

  const handleInput = (key, e) => {
    let value = e.target.value;
    if (key === "active" || key === "lmb") {
      value = e.target.checked;
    }

    setErrors(errors.filter((k) => k !== key));

    if (key === "sms") {
      setMsg({ ...msg, sms: true, email: false });
    } else if (key === "email") {
      setMsg({
        ...msg,
        email: true,
        sms: false,
        from_name: msg.from_name || settings.branding.from_name,
        from_email: msg.from_email || settings.branding.from_email,
      });
    } else if (key === "send_day") {
      if (+value === 99998) {
        setMsg({ ...msg, send_day: value, email: true, sms: false });
      } else {
        setMsg({ ...msg, send_day: value });
      }
    } else {
      setMsg({ ...msg, [key]: value });
    }
  };

  // Helper to update msg and msgByType for current scheduleType
  const updateMsgAndStore = (newMsg, type) => {
    setMsg(newMsg);
    setMsgByType((prev) => ({ ...prev, [type || scheduleType]: newMsg }));
  };

  const handleInputs = (keys) => {
    let newMsg = JSON.parse(JSON.stringify(msg));
    for (let x in keys) {
      newMsg[x] = keys[x];
    }
    updateMsgAndStore(newMsg);
  };

  const handleOption = (key, e) => {
    setMsg({ ...msg, options: { ...msg.options, [key]: e.target.value } });
  };

  const optionLabel = (key) => {
    let value = key === "min_gap" ? 0 : 99999;

    if (options !== null && typeof options === "object") {
      value = +options[key];
    }

    if (key === "min_gap" && value === 0) {
      return "No Min";
    }

    if (key === "max_gap" && value === 99999) {
      return "No Max";
    }

    return `${value} ${value === 1 ? "Night" : "Nights"}`;
  };

  const hasSource = (source, e) => {
    return (msg.sources || []).includes(source);
  };

  const handleSource = (e) => {
    let sources = [...(msg.sources || [])];
    let source = e.target.value;
    let idx = (msg.sources || []).indexOf(source);

    if (idx === -1) {
      sources.push(source);
    } else {
      sources.splice(idx, 1);
    }

    setMsg({ ...msg, sources: sources });
  };

  const handleSelectTemplate = (val) => {
    if (!val) {
      return setTemplate("");
    }

    setTemplate(val);
    setMsg({ ...msg, body: val.body, subject: val.subject });
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    let isValid = doValidate();
    if (!isValid) {
      return;
    }

    if (isNew == true) {
      let result = await insertScheduledMessage(msg);
      if (result) {
        UIkit.offcanvas(offcanvasRef.current).hide();
        UIkit.notification("Scheduled Message Added", "success");
      }
    } else {
      let result = await updateScheduledMessage(msg);
      if (result) {
        UIkit.offcanvas(offcanvasRef.current).hide();
        UIkit.notification("Scheduled Message Updated", "success");
      }
    }
  };

  const handleClose = () => {
    setTemplate("");
    initMessage();
  };

  const capitalize = (str) => {
    if (!str) {
      return "";
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const hasFlag = (type, value) => {
    let { flags } = msg;
    if (type === "balance" && value === "") {
      return !(flags || []).find((f) => {
        return f === "balance_pending" || f === "paid_in_full";
      });
    }

    if (type === "contract" && value === "") {
      return !(flags || []).find((f) => {
        return f === "contract_signed" || f === "contract_pending";
      });
    }

    if (type === "guest_id_verification" && value === "") {
      return !(flags || []).find((f) => {
        return (
          f === "guest_id_verification_pending" ||
          f === "guest_id_verification_completed"
        );
      });
    }

    return !!(flags || []).find((f) => {
      return f === value;
    });
  };

  const addFlag = (type, value) => {
    let flags = msg.flags || [];
    if (value === "") {
      if (type === "balance") {
        flags = (flags || []).filter(
          (f) => !["balance_pending", "paid_in_full"].includes(f),
        );
      } else if (type === "contract") {
        flags = (flags || []).filter(
          (f) => !["contract_signed", "contract_pending"].includes(f),
        );
      } else if (type === "guest_id_verification") {
        flags = (flags || []).filter(
          (f) =>
            ![
              "guest_id_verification_pending",
              "guest_id_verification_completed",
            ].includes(f),
        );
      }
    } else if (!(flags || []).find((f) => f === value)) {
      if (type === "balance") {
        flags = (flags || []).filter(
          (f) => ["balance_pending", "paid_in_full"].indexOf(f) === -1,
        );
        flags.push(value);
      }

      if (type === "contract") {
        flags = (flags || []).filter(
          (f) => ["contract_signed", "contract_pending"].indexOf(f) === -1,
        );
        flags.push(value);
      }

      if (type === "guest_id_verification") {
        flags = (flags || []).filter(
          (f) =>
            [
              "guest_id_verification_pending",
              "guest_id_verification_completed",
            ].indexOf(f) === -1,
        );
        flags.push(value);
      }
    }

    setMsg({ ...msg, flags: flags });
  };

  const advancedConditions = getAdvancedConditions(showPmsAdvancedOptions, settings);
  // /**
  //  * Add Tag Name to newMessage.tags if tag is not exist in newMessage.tags array &
  //  * Remove Tag Name from newMessage.tags if tag is already exist in newMessage.tags array
  //  * @param {String} tag Tag Name
  //  */
  // /*const handleAddTag = (tag) => {
  //  if (tempMSG.tags.indexOf(tag) === -1) {
  //  schedulerCtx.setTempMSG({ ...tempMSG, tags: [...tempMSG.tags, tag] });
  //  } else {
  //  schedulerCtx.setTempMSG({
  //    ...tempMSG,
  //    tags: tempMSG.tags.filter((item) => item !== tag),
  //  });
  //  }
  // };*/

  // console.log(msg);

  return (
    <>
      <div
        id="scheduler-add"
        ref={offcanvasRef}
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              uk-close="true"
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">
                {isNew === true ? "Add a" : "Edit"} Scheduled Message
              </h2>
            </div>
            <div className="uk-modal-body uk-flex">
              <div className="addScheduleMsgForm">
                <form
                  className="uk-grid-small uk-margin-none-top"
                  uk-grid="true"
                  onSubmit={handleRegister}
                >
                  <div className="uk-width-1-2">
                    <label
                      className="uk-form-label"
                      htmlFor="txt-schedule-name"
                    >
                      Name*
                    </label>
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: !!errors.includes("name"),
                        })}
                        id="txt-schedule-name"
                        value={msg.name}
                        onInput={handleInput.bind(null, "name")}
                      />
                    </InputContainer>
                  </div>
                  <div className="uk-width-1-2">
                    <div className="uk-form-label uk-text-center">Active</div>
                    <div className="autosend-checkbox">
                      <label
                        htmlFor="scheduleNameActive"
                        className="setting-switch"
                      >
                        <input
                          type="checkbox"
                          className="uikit-checkbox"
                          id="scheduleNameActive"
                          checked={msg.active || false}
                          onChange={handleInput.bind(null, "active")}
                        />
                        <span className="setting-slider setting-round"></span>
                      </label>
                    </div>
                  </div>
                  <SelectTemplate
                    onChange={handleSelectTemplate}
                    template={template}
                  />
                  <div className="uk-width-1-1">
                    <div className="hms-add-tag">
                      <div className="uk-form-label">Tags</div>
                      <div className="uk-flex uk-flex-top">
                        <Tags
                          value={msg.tags || []}
                          onChange={(tags) =>
                            handleInput("tags", { target: { value: tags } })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <>
                    <div className="uk-width-1-1">
                      <div className="uk-form-label">Schedule Type</div>
                      <div className="uk-grid uk-form-controls">
                        <label>
                          <input
                            type="radio"
                            className="uk-radio"
                            name="schedule-type-radio"
                            checked={scheduleType === "unscheduled"}
                            onChange={() => {
                              // Store current msg for current type
                              setMsgByType((prev) => ({ ...prev, [scheduleType]: msg }));
                              setScheduleType("unscheduled");
                              // Restore if exists, else use default
                              setMsg(
                                msgByType["unscheduled"] || {
                                  ...msg,
                                  send_day: 99999,
                                  reference_direction: "before",
                                  reference_point: "in",
                                  send_time: "10:00 AM",
                                  options: null,
                                  sources: [],
                                }
                              );
                            }}
                          />{" "}
                          Unscheduled
                        </label>

                        <label>
                          <input
                            type="radio"
                            className="uk-radio"
                            name="schedule-type-radio"
                            checked={scheduleType === "check-in-out"}
                            onChange={() => {
                              setMsgByType((prev) => ({ ...prev, [scheduleType]: msg }));
                              setScheduleType("check-in-out");
                              setMsg(
                                msgByType["check-in-out"] || {
                                  ...msg,
                                  send_day: 0,
                                  reference_direction: "before",
                                  reference_point: "in",
                                  send_time: "10:00 AM",
                                  options: null,
                                }
                              );
                            }}
                          />{" "}
                          Scheduled
                        </label>

                        <label>
                          <input
                            type="radio"
                            className="uk-radio"
                            name="schedule-type-radio"
                            checked={scheduleType === "extend"}
                            onChange={() => {
                              setMsgByType((prev) => ({ ...prev, [scheduleType]: msg }));
                              setScheduleType("extend");
                              setMsg(
                                msgByType["extend"] || {
                                  ...msg,
                                  send_day: 99900,
                                  reference_direction: "before",
                                  reference_point: "in",
                                  send_time: "11:00 AM",
                                  lmb: false,
                                  options: {
                                    min_gap: 0,
                                    max_gap: 99999,
                                    extend_type: "check_in_extension",
                                  },
                                }
                              );
                            }}
                          />{" "}
                          Extend
                        </label>

                        <label>
                          <input
                            type="radio"
                            className="uk-radio"
                            name="schedule-type-radio"
                            checked={scheduleType === "day-of-week"}
                            onChange={() => {
                              setMsgByType((prev) => ({ ...prev, [scheduleType]: msg }));
                              setScheduleType("day-of-week");
                              setMsg(
                                msgByType["day-of-week"] || {
                                  ...msg,
                                  send_day: 99997,
                                  reference_direction: "before",
                                  reference_point: "monday",
                                  send_time: "10:00 AM",
                                  options: null,
                                }
                              );
                            }}
                          />{" "}
                          Day of Week
                        </label>

                        <label>
                          <input
                            type="radio"
                            className="uk-radio"
                            name="schedule-type-radio"
                            checked={scheduleType === "email-upon-booking"}
                            onChange={() => {
                              setMsgByType((prev) => ({ ...prev, [scheduleType]: msg }));
                              setScheduleType("email-upon-booking");
                              setMsg(
                                msgByType["email-upon-booking"] || {
                                  ...msg,
                                  sms: false,
                                  email: true,
                                  send_day: 99998,
                                  reference_point: "in",
                                  reference_direction: "before",
                                  options: null,
                                }
                              );
                            }}
                          />{" "}
                          Email Upon Booking
                        </label>
                      </div>
                    </div>

                    {scheduleType !== "email-upon-booking" && (
                      <>
                        <div className="uk-width-1-1">
                          <div className="uk-form-label">Send By</div>
                          <div className="uk-grid uk-form-controls">
                            <label className="uk-margin-right">
                              <input
                                type="radio"
                                className="uk-radio"
                                name="send-type-radio"
                                checked={msg.sms}
                                onChange={handleInput.bind(null, "sms")}
                                disabled={Math.abs(msg.send_day) == "99998"}
                              />{" "}
                              SMS
                            </label>
                            <label>
                              <input
                                type="radio"
                                className="uk-radio"
                                name="send-type-radio"
                                checked={msg.email}
                                onChange={handleInput.bind(null, "email")}
                              />{" "}
                              Email
                            </label>
                          </div>
                        </div>
                      </>
                    )}

                    {scheduleType == "check-in-out" && (
                      <>
                        <div className="uk-width-1-4@s scheduler-schedule-field">
                          <label className="uk-form-label">Schedule</label>
                          <div className="uk-form-controls">
                            <div uk-form-custom="target: > * > span:first-child">
                              <select
                                value={Math.abs(msg.send_day)}
                                onInput={handleInput.bind(null, "send_day")}
                              >
                                {[...Array(30)].map((_, index) => {
                                  if (index === 0) {
                                    return (
                                      <option
                                        value={0}
                                        key={`relative_${index}`}
                                      >
                                        Day of
                                      </option>
                                    );
                                  }
                                  if (index === 1) {
                                    return (
                                      <option
                                        value={index}
                                        key={`relative_${index}`}
                                      >
                                        {index} Day
                                      </option>
                                    );
                                  }
                                  return (
                                    <option
                                      value={index}
                                      key={`relative_${index}`}
                                    >
                                      {index} Days
                                    </option>
                                  );
                                })}
                              </select>
                              <button
                                className="uk-button uk-button-default"
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                                type="button"
                                tabIndex="-1"
                              >
                                <span>
                                  {Math.abs(msg.send_day) === 99999
                                    ? "Unscheduled"
                                    : Math.abs(msg.send_day) >= 99990 &&
                                        Math.abs(msg.send_day) < 99998
                                      ? `${
                                          99997 - Math.abs(msg.send_day)
                                        } Day(s) Before`
                                      : Math.abs(msg.send_day) === 1
                                        ? `${Math.abs(msg.send_day)} Day`
                                        : Math.abs(msg.send_day) === 0
                                          ? "Day of"
                                          : `${Math.abs(msg.send_day)} Days`}
                                </span>
                                <span
                                  uk-icon="icon: chevron-down"
                                  className="hms-align-right"
                                ></span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {scheduleType === "extend" && (
                      <>
                        <div className="uk-width-1-1">
                          <div className="uk-inline uk-margin-right">
                            <label className="uk-form-label">Extend</label>
                            <div>
                              <label className="uk-margin-right">
                                <input
                                  type="radio"
                                  className="uk-radio"
                                  name="extend-radio"
                                  value="check_in_extension"
                                  onChange={(e) =>
                                    handleOption("extend_type", e)
                                  }
                                  checked={
                                    options?.extend_type ===
                                    "check_in_extension"
                                  }
                                />{" "}
                                Early Check-In
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  className="uk-radio"
                                  name="extend-radio"
                                  value="check_out_extension"
                                  onChange={(e) =>
                                    handleOption("extend_type", e)
                                  }
                                  checked={
                                    options?.extend_type ===
                                    "check_out_extension"
                                  }
                                />{" "}
                                Late Check-Out
                              </label>
                            </div>
                          </div>

                          <div className="uk-inline uk-margin-right">
                            <label className="uk-form-label">Min Gap</label>
                            <div className="uk-form-controls">
                              <div className="uk-form-custom">
                                <select
                                  value={options?.min_gap ?? 0}
                                  onInput={(e) => handleOption("min_gap", e)}
                                >
                                  <option value={0}>0 Nights</option>
                                  {[...new Array(6)].map((_, index) => (
                                    <option
                                      value={`${index + 1}`}
                                      key={`gap_${index}`}
                                    >
                                      {`${index + 1} ${index + 1 === 1 ? "Night" : "Nights"}`}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className="uk-button uk-button-default"
                                  type="button"
                                  tabIndex="-1"
                                >
                                  <span>{optionLabel("min_gap")}</span>
                                  <span uk-icon="icon: chevron-down"></span>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="uk-inline uk-margin-right">
                            <label className="uk-form-label">Max Gap</label>
                            <div className="uk-form-controls">
                              <div className="uk-form-custom">
                                <select
                                  value={msg?.options?.max_gap ?? 99999}
                                  onInput={(e) => handleOption("max_gap", e)}
                                >
                                  <option value={99999}>No Max</option>
                                  {[...new Array(6)].map((_, index) => (
                                    <option
                                      value={`${index + 1}`}
                                      key={`gap_${index}`}
                                    >
                                      {`${index + 1} ${index + 1 === 1 ? "Night" : "Nights"}`}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className="uk-button uk-button-default"
                                  type="button"
                                  tabIndex="-1"
                                >
                                  <span>{optionLabel("max_gap")}</span>
                                  <span uk-icon="icon: chevron-down"></span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {scheduleType === "day-of-week" && (
                      <>
                        <div className="uk-width-1-3@s uk-margin-small-right">
                          <label className="uk-form-label">
                            Day of Week Schedule
                          </label>
                          <div className="uk-form-controls">
                            <div uk-form-custom="target: > * > span:first-child">
                              <select
                                value={Math.abs(msg.send_day)}
                                onInput={handleInput.bind(null, "send_day")}
                              >
                                <optgroup label="Day of Week" />
                                {[...Array(8)].map((_, index) => (
                                  <option
                                    value={99997 - index}
                                    key={`week_${index}`}
                                  >
                                    {index} Day(s) Before
                                  </option>
                                ))}
                              </select>
                              <button
                                className="uk-button uk-button-default"
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                                type="button"
                                tabIndex="-1"
                              >
                                <span>
                                  {Math.abs(msg.send_day) === 99999
                                    ? "Unscheduled"
                                    : Math.abs(msg.send_day) >= 99990 &&
                                        Math.abs(msg.send_day) < 99998
                                      ? `${
                                          99997 - Math.abs(msg.send_day)
                                        } Day(s) Before`
                                      : Math.abs(msg.send_day) === 1
                                        ? `${Math.abs(msg.send_day)} Day`
                                        : Math.abs(msg.send_day) === 0
                                          ? "Day of"
                                          : `${Math.abs(msg.send_day)} Days`}
                                </span>
                                <span
                                  uk-icon="icon: chevron-down"
                                  className="hms-align-right"
                                ></span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="uk-margin-small-right">
                          <label className="uk-form-label">On</label>
                          <div className="uk-form-controls">
                            <div uk-form-custom="target: > * > span:first-child">
                              <select
                                value={msg.reference_point}
                                onInput={handleInput.bind(
                                  null,
                                  "reference_point",
                                )}
                              >
                                <option value="monday">Monday</option>
                                <option value="tuesday">Tuesday</option>
                                <option value="wednesday">Wednesday</option>
                                <option value="thursday">Thursday</option>
                                <option value="friday">Friday</option>
                                <option value="saturday">Saturday</option>
                                <option value="sunday">Sunday</option>
                              </select>
                              <button
                                className="uk-button uk-button-default"
                                type="button"
                                tabIndex="-1"
                              >
                                <span>{capitalize(msg.reference_point)}</span>
                                <span
                                  uk-icon="icon: chevron-down"
                                  className="hms-align-right"
                                ></span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {(scheduleType === "day-of-week" ||
                      scheduleType === "check-in-out" ||
                      scheduleType === "email-upon-booking" ||
                      scheduleType === "extend") && (
                      <>
                        <div className={scheduleType === "check-in-out"
                          ? "uk-width-3-4@s scheduler-timing-fields"
                          : "uk-width-1-1"}>
                          {(scheduleType === "check-in-out" ||
                            scheduleType === "extend") && (
                            <>
                              {scheduleType === "extend" && (
                                <>
                                  <div className="uk-inline uk-margin-right">
                                    <label className="uk-form-label">
                                      Send Day
                                    </label>
                                    <div className="uk-form-controls">
                                      <div uk-form-custom="target: > * > span:first-child">
                                        <select
                                          value={Math.abs(msg.send_day)}
                                          onInput={handleInput.bind(
                                            null,
                                            "send_day",
                                          )}
                                        >
                                          {[...Array(30)].map((_, index) => {
                                            if (index === 0) {
                                              return (
                                                <option
                                                  value={99900}
                                                  key={`relative_${index}`}
                                                >
                                                  Day of
                                                </option>
                                              );
                                            }
                                            if (index === 1) {
                                              return (
                                                <option
                                                  value={99900 + index}
                                                  key={`relative_${index}`}
                                                >
                                                  {index} Day
                                                </option>
                                              );
                                            }
                                            return (
                                              <option
                                                value={99900 + index}
                                                key={`relative_${index}`}
                                              >
                                                {index} Days
                                              </option>
                                            );
                                          })}
                                        </select>
                                        <button
                                          className="uk-button uk-button-default"
                                          type="button"
                                          tabIndex="-1"
                                        >
                                          <span>
                                            {msg.reference_direction
                                              ?.charAt(0)
                                              .toUpperCase() +
                                              msg.reference_direction?.slice(1)}
                                          </span>
                                          <span
                                            uk-icon="icon: chevron-down"
                                            className="hms-align-right"
                                          ></span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                              {msg.send_day !== 0 &&
                                scheduleType !== "extend" && (
                                  <>
                                    <div className="uk-inline uk-margin-right">
                                      <label className="uk-form-label">
                                        Before/After
                                      </label>
                                      <div className="uk-form-controls">
                                        <div uk-form-custom="target: > * > span:first-child">
                                          <select
                                            value={msg.reference_direction}
                                            onInput={handleInput.bind(
                                              null,
                                              "reference_direction",
                                            )}
                                          >
                                            <option value="before">
                                              Before
                                            </option>
                                            <option value="after">After</option>
                                          </select>
                                          <button
                                            className="uk-button uk-button-default"
                                            type="button"
                                            tabIndex="-1"
                                          >
                                            <span>
                                              {msg.reference_direction
                                                ?.charAt(0)
                                                .toUpperCase() +
                                                msg.reference_direction?.slice(
                                                  1,
                                                )}
                                            </span>
                                            <span
                                              uk-icon="icon: chevron-down"
                                              className="hms-align-right"
                                            ></span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}

                              <div className="uk-inline uk-margin-right">
                                <label className="uk-form-label">
                                  {scheduleType !== "extend"
                                    ? "Check-In/Out"
                                    : "Before"}
                                </label>
                                <div className="uk-form-controls">
                                  <div uk-form-custom="target: > * > span:first-child">
                                    <select
                                      value={msg.reference_point}
                                      onInput={handleInput.bind(
                                        null,
                                        "reference_point",
                                      )}
                                    >
                                      <option value="in">Check-In</option>
                                      <option value="out">Check-Out</option>
                                    </select>
                                    <button
                                      className="uk-button uk-button-default"
                                      type="button"
                                      tabIndex="-1"
                                    >
                                      <span>{`Check-${capitalize(
                                        msg.reference_point,
                                      )}`}</span>
                                      <span
                                        uk-icon="icon: chevron-down"
                                        className="hms-align-right"
                                      ></span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {scheduleType !== "email-upon-booking" && (
                            <>
                              <div className="uk-inline uk-margin-right">
                                <label className="uk-form-label">At</label>
                                <div className="uk-form-controls">
                                  <div uk-form-custom="target: > * > span:first-child">
                                    <select
                                      value={msg.send_time}
                                      onInput={handleInput.bind(
                                        null,
                                        "send_time",
                                      )}
                                    >
                                      <option value="">Select a Time</option>
                                      {[...Array(24 * 4)].map((_, index) => {
                                        var strTime = new Date(
                                          0,
                                          0,
                                          0,
                                          index / 4,
                                          (index % 4) * 15,
                                        ).toLocaleString("en-US", {
                                          hour: "numeric",
                                          minute: "2-digit",
                                          hour12: true,
                                        });
                                        return (
                                          <option
                                            value={strTime}
                                            key={`time_${index}`}
                                          >
                                            {strTime}
                                          </option>
                                        );
                                      })}
                                    </select>
                                    <button
                                      className="uk-button uk-button-default"
                                      type="button"
                                      tabIndex="-1"
                                    >
                                      <span>{msg.send_time}</span>
                                      <span
                                        uk-icon="icon: chevron-down"
                                        className="hms-align-right"
                                      ></span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                  {(msg.email || scheduleType === "email-upon-booking") && (
                    <div className="uk-width-1-1 scheduler-email-fields">
                      <div className="scheduler-email-field">
                        <label className="uk-form-label">From Name</label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            type="text"
                            value={msg.from_name}
                            onInput={handleInput.bind(null, "from_name")}
                          />
                        </div>
                      </div>
                      <div className="scheduler-email-field">
                        <label className="uk-form-label">
                          From Email Address
                        </label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            type="text"
                            value={msg.from_email}
                            onInput={handleInput.bind(null, "from_email")}
                          />
                        </div>
                      </div>

                      <div className="scheduler-email-field">
                        <label className="uk-form-label">BCC Email</label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            type="text"
                            value={msg.bcc}
                            onInput={handleInput.bind(null, "bcc")}
                          />
                        </div>
                      </div>
                      <div className="scheduler-email-field">
                        <label className="uk-form-label">Subject</label>
                        <div className="uk-form-controls">
                          <input
                            className="uk-input"
                            type="text"
                            value={msg.subject}
                            onInput={handleInput.bind(null, "subject")}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="uk-width-1-1">
                    <SMSEmailInputbox
                      error={errors.includes("body")}
                      value={msg.body || ""}
                      limit={msg.sms}
                      macrosOrResponse={true}
                      allowIdentityVerification={true}
                      onChange={handleInput.bind(null, "body")}
                      optimizeWithAI={true}
                      optimizationSource="scheduler"
                      optimizationTrigger="icon"
                      normalizeOnPaste={msg.sms}
                      messageLengthStatus={
                        msg.sms ? (
                          <MessageLengthInlineStatus analysis={messageLength} hideGood />
                        ) : null
                      }
                    />
                    {msg.sms && (
                      <MessageLengthEditorStatus analysis={messageLength} />
                    )}
                  </div>
                  <ul
                    uk-accordion="multiple: true"
                    className="scheduler-advanced-options"
                  >
                    <li>
                      <div className="uk-accordion-title" href>
                        Booking Sources
                      </div>
                      <div className="uk-accordion-content">
                        <p className="secondary">
                          Select booking sources or leave blank to send to all.
                          View your booking mappings <a href="/reports/booking-source-mapping">here</a>.
                        </p>
                        <section
                          id="source-restrictions"
                          className="uk-width-1-1"
                        >
                          <div className="uk-width-1-1">
                            <div className="uk-form-controls">
                              <div className="uk-column-1-3">
                                {(sources || []).map((source, idx) => {
                                  return (
                                    <div className="uk-form-controls">
                                      <input
                                        className="uk-checkbox"
                                        id={`source-${idx}`}
                                        type="checkbox"
                                        name="sources"
                                        placeholder=""
                                        checked={hasSource(source)}
                                        onInput={handleSource}
                                        value={source}
                                      />
                                      <label
                                        className="uk-label"
                                        for={`source-${idx}`}
                                      >
                                        {source}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </section>
                      </div>
                    </li>
                  </ul>
                  {advancedConditions.length > 0 && <ul
                    uk-accordion="multiple: true"
                    className="scheduler-advanced-options"
                  >
                    <li className=" uk-open">
                      <div className="uk-accordion-title" href>
                        Advanced Options
                      </div>
                      <div className="uk-accordion-content">
                        <div className="uk-width-1-1">
                          <div className="scheduler-condition-grid">
                            {advancedConditions.map((condition) => (
                              <div
                                className="scheduler-condition-row"
                                key={condition.type}
                              >
                                <div className="scheduler-condition-label">
                                  {condition.label}
                                </div>
                                <div className="scheduler-condition-options">
                                  {condition.options.map((option) => {
                                    const selected = hasFlag(
                                      condition.type,
                                      option.value,
                                    );

                                    return (
                                      <label
                                        className="scheduler-condition-option"
                                        key={option.value || "ignore"}
                                      >
                                        <input
                                          type="radio"
                                          className="uk-radio"
                                          name={`send-${condition.type}-radio`}
                                          checked={selected}
                                          onChange={addFlag.bind(
                                            null,
                                            condition.type,
                                            option.value,
                                          )}
                                        />
                                        <span>{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                          {showPmsAdvancedOptions && (
                            <p className="secondary uk-margin-top">
                              * Airbnb bookings are excluded from these rules.
                              Contact RueBaRue Support to override this setting.
                            </p>
                          )}
                        </div>
                      </div>
                    </li>
                  </ul>}

                  {!(msg.send_day <= 99997 && msg.send_day >= 99900) && (
                    <div className="uk-width-1-1 uk-margin-top">
                      <div className="uk-flex">
                        <div className="autosend-checkbox uk-form-controls">
                          <label
                            htmlFor="lastMinuteBooking"
                            className="setting-switch"
                          >
                            <input
                              type="checkbox"
                              id="lastMinuteBooking"
                              checked={msg.lmb || false}
                              onChange={handleInput.bind(null, "lmb")}
                            />
                            <span className="setting-slider setting-round"></span>
                          </label>
                        </div>
                        <div className="m-l-10">
                          <div>
                            Include in Last Minute Bookings{" "}
                            <a href="/settings/messages">
                              Messaging Hours{" "}
                              <img
                                className="info-icon"
                                src="/images/info-icon.svg"
                                alt="info"
                                uk-tooltip="This message will be sent to guests who made last minute within the messaging hours. See Settings > Messages > Messaging Hour."
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <button
                      className="uk-button hms-btn hms-red-btn"
                      type="submit"
                    >
                      {loading == true ? (
                        <div data-uk-spinner="ratio: 0.5;"></div>
                      ) : (
                        "Save"
                      )}
                    </button>
                    &nbsp;
                    <button
                      className="uk-button hms-btn uk-offcanvas-close"
                      onClick={handleClose}
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                {!isNew && (
                  <>
                    {message?.updated_at && (
                      <div className="s-msg-last-updated">
                        <ConvertUTCDateToLocalDate
                          date={message.updated_at}
                          label="Last Updated: "
                        />
                      </div>
                    )}
                    {message?.created_at && (
                      <div className="s-msg-last-updated">
                        <ConvertUTCDateToLocalDate
                          date={message.created_at}
                          label="Added at: "
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="schedule-modal-right">
                <div className="schedule-modal-right-inner">
                  <div>
                    Message will be sent to guests staying at these{" "}
                    <b>{filteredRentals.length} properties</b>.
                  </div>
                  <div className="properties-w-home-icon">
                    {filteredRentals?.map(
                      (rental, index) =>
                        rental && (
                          <div
                            className="uk-flex uk-flex-middle"
                            key={`scheduler_rental_${index}`}
                          >
                            <div>
                              <img
                                src="/images/home.svg"
                                alt="home"
                                width="30px"
                                height="30px"
                              />
                            </div>
                            <div uk-tooltip={rental.pms_id}>
                              {rental.pms_id?.length > 30
                                ? `${rental.pms_id.slice(0, 30)}...`
                                : rental.pms_id}
                            </div>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchedulerAdd;
